import { io, Socket } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private messageHandlers: Map<string, Set<(data: any) => void>> = new Map();

  async connect() {
    if (this.socket?.connected) return;

    const token = localStorage.getItem('token');
    
    this.socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true
    });

    this.socket.on('connect', async () => {
      console.log('Socket connected');
      this.isConnected = true;
      
      // Authenticate
      if (token) {
        this.socket?.emit('authenticate', { token }, (response: any) => {
          console.log('Auth response:', response);
        });
      }
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.isConnected = false;
    });

    // Set up event handlers
    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    // New message received
    this.socket.on('new_message', (data) => {
      this.emit('new_message', data);
    });

    // Typing indicators
    this.socket.on('user_typing', (data) => {
      this.emit('user_typing', data);
    });

    this.socket.on('user_stopped_typing', (data) => {
      this.emit('user_stopped_typing', data);
    });

    // Match notifications
    this.socket.on('match_accepted', (data) => {
      this.emit('match_accepted', data);
    });

    // Location sharing
    this.socket.on('location_shared', (data) => {
      this.emit('location_shared', data);
    });

    // WebRTC signaling
    this.socket.on('participant_joined', (data) => {
      this.emit('participant_joined', data);
    });

    this.socket.on('participant_left', (data) => {
      this.emit('participant_left', data);
    });

    this.socket.on('webrtc_offer', (data) => {
      this.emit('webrtc_offer', data);
    });

    this.socket.on('webrtc_answer', (data) => {
      this.emit('webrtc_answer', data);
    });

    this.socket.on('webrtc_ice_candidate', (data) => {
      this.emit('webrtc_ice_candidate', data);
    });

    this.socket.on('stream_ended', (data) => {
      this.emit('stream_ended', data);
    });

    // Reaction updates
    this.socket.on('reaction_update', (data) => {
      this.emit('reaction_update', data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Event subscription
  on(event: string, handler: (data: any) => void) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, new Set());
    }
    this.messageHandlers.get(event)?.add(handler);
    
    return () => {
      this.messageHandlers.get(event)?.delete(handler);
    };
  }

  private emit(event: string, data: any) {
    this.messageHandlers.get(event)?.forEach(handler => handler(data));
  }

  // Chat methods
  joinEventChat(eventId: string) {
    return new Promise((resolve) => {
      this.socket?.emit('join_event_chat', { event_id: eventId }, resolve);
    });
  }

  leaveEventChat(eventId: string) {
    return new Promise((resolve) => {
      this.socket?.emit('leave_event_chat', { event_id: eventId }, resolve);
    });
  }

  sendMessage(eventId: string, content: string, mediaUrl?: string, mediaType?: string, replyTo?: string) {
    return new Promise((resolve) => {
      this.socket?.emit('send_message', {
        event_id: eventId,
        content,
        media_url: mediaUrl,
        media_type: mediaType,
        reply_to: replyTo
      }, resolve);
    });
  }

  startTyping(eventId: string) {
    this.socket?.emit('typing_start', { event_id: eventId });
  }

  stopTyping(eventId: string) {
    this.socket?.emit('typing_stop', { event_id: eventId });
  }

  markMessagesRead(messageIds: string[]) {
    return new Promise((resolve) => {
      this.socket?.emit('mark_read', { message_ids: messageIds }, resolve);
    });
  }

  // Reaction methods
  addReaction(messageId: string, emoji: string, eventId: string) {
    return new Promise((resolve) => {
      this.socket?.emit('add_reaction', { 
        message_id: messageId, 
        emoji, 
        event_id: eventId 
      }, resolve);
    });
  }

  // WebRTC methods
  joinStreamRoom(roomCode: string) {
    return new Promise((resolve) => {
      this.socket?.emit('join_stream_room', { room_code: roomCode }, resolve);
    });
  }

  leaveStreamRoom(roomCode: string) {
    return new Promise((resolve) => {
      this.socket?.emit('leave_stream_room', { room_code: roomCode }, resolve);
    });
  }

  sendOffer(targetSid: string, offer: RTCSessionDescriptionInit) {
    this.socket?.emit('webrtc_offer', { target: targetSid, offer });
  }

  sendAnswer(targetSid: string, answer: RTCSessionDescriptionInit) {
    this.socket?.emit('webrtc_answer', { target: targetSid, answer });
  }

  sendIceCandidate(targetSid: string, candidate: RTCIceCandidateInit) {
    this.socket?.emit('webrtc_ice_candidate', { target: targetSid, candidate });
  }

  getSocket() {
    return this.socket;
  }

  getConnectionStatus() {
    return this.isConnected;
  }
}

export const socketService = new SocketService();
